import React from 'react';
import { Box, Card, CardContent, Typography, Avatar, CircularProgress } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import ModernButton from './ModernButton';

/**
 * EmptyState - Consistent empty state display
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.title - Title text
 * @param {string} props.message - Message text
 * @param {string} props.actionLabel - Action button label
 * @param {Function} props.onAction - Action button callback
 * @param {React.ReactNode} props.actionIcon - Icon for action button
 * @param {string} props.iconColor - Background color for icon avatar
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.loadingText - Loading text
 */
const EmptyState = ({
  icon,
  title,
  message,
  actionLabel,
  onAction,
  actionIcon,
  iconColor = 'grey.300',
  loading = false,
  loadingText = 'Loading...',
}) => {
  if (loading) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 8, textAlign: 'center' }}>
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            {loadingText}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 8, textAlign: 'center' }}>
        <Avatar
          sx={{
            backgroundColor: iconColor,
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon || <InfoIcon sx={{ fontSize: 32 }} />}
        </Avatar>
        <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
          {title}
        </Typography>
        {message && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: actionLabel ? 3 : 0 }}>
            {message}
          </Typography>
        )}
        {actionLabel && onAction && (
          <ModernButton
            onClick={onAction}
            startIcon={actionIcon}
            variant="contained"
          >
            {actionLabel}
          </ModernButton>
        )}
      </CardContent>
    </Card>
  );
};

export default EmptyState;
